/**
 * The Funrooms: soft play and party dressing.
 *
 * A soft-play centre and a function suite in the same building, both left
 * running with nobody in them. Everything here was set out for a children's
 * party that finished a long time ago, and the register every model in this
 * file has to hold is *cheerful*: bright, saturated, wipe-clean and completely
 * still. Nothing is smashed, nothing is rotting and nothing winks. The horror
 * is that somewhere built for pleasure is empty and running.
 *
 * That sets three rules the rest of the catalogue does not have.
 *
 *  - **Primaries survive the level tint.** Ordinary scenery is pulled a third of
 *    the way into the level's own air by `belong`, which is what stops a
 *    cardboard box looking imported. Play equipment is the one thing here that
 *    was *chosen* to be loud, so it goes through `party()` instead — about a
 *    third of the usual pull, the same treatment `PropPalette.hazard` gets — and
 *    comes out red, yellow, blue and green under the Funrooms' pink light.
 *  - **Wiped down, not dirty.** The four big moulded surfaces (frame padding,
 *    slide bed, balls, castle vinyl) sit on a low-roughness standard material so
 *    a torch leaves a highlight on them. Everything else is Lambert. Sticky
 *    rather than grimy is the whole difference between this level and Level 1.
 *  - **Silhouette at room scale.** These are the biggest props in the game after
 *    the chute, and a soft-play hall is thirty metres across. A play structure
 *    has to read as a climbing frame from the door of the room, so posts, decks
 *    and netting are sized for that and detail is spent nowhere else. Nobody
 *    will ever be close enough to one to want more.
 *
 * Every kind below is centred on its own origin in XZ and sized to the collision
 * box in `world/gen/propTables.ts`, because these props are solid: a model wider
 * than its box is an object the player walks into the middle of, and a box wider
 * than its model is an invisible wall. The box is axis-aligned and does *not*
 * turn with the render-only yaw jitter, so the big square-plan kinds either
 * carry no jitter at all or keep every part inside a circle the box contains.
 * Re-measure if either moves.
 *
 * The two fit-out modules are the deliberate exception, and it is worth reading
 * before touching either. A frame module is three and a half metres wide and its
 * box is a third of a metre, because everything outside the box is at 1.98 m or
 * higher and the player walks under it; a ball field is seven metres wide and
 * has no box at all, because the player wades through it. On both of them the
 * rule is not "stay inside the box" but **"stay out of the first two metres of
 * air outside it"**, and anything added here that breaks that is an invisible
 * wall in the middle of the level's own floor.
 */

import * as THREE from 'three';
import { clamp01 } from '../core/math';
import { makeRNG } from '../core/rng';
import type { LevelSpec } from '../core/types';
import {
  belong,
  box,
  css,
  cyl,
  makeShape,
  matte,
  merge,
  propPalette,
  settle,
  slab,
  strut,
  tumble,
  wallDecal,
  type PropLayer,
  type PropPalette,
  type PropShape,
} from './propkit';
import { BANNER_LINING_FROM, BANNER_METRES, makeBannerAtlas, makeConfettiTexture } from './propfundecals';

/* ------------------------------------------------------------------ *
 * Shared helpers
 * ------------------------------------------------------------------ */

function layer(material: THREE.Material, variants: Array<THREE.BufferGeometry | null>): PropLayer {
  return { material, variants };
}

/** A capped cylinder lying along X: rungs, bumpers, anything crossing a frame. */
function rodX(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1);
  g.rotateZ(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/** An *open* cylinder lying along Z: a sleeve, not a plug. */
function tubeZ(r: number, len: number, seg: number, y: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1, true);
  g.rotateX(Math.PI / 2);
  if (y) g.translate(0, y, 0);
  return g;
}

/**
 * A low-poly ellipsoid. Balloons, balls and every slumped fold of vinyl in this
 * file are one of these: at these radii a six-sided sphere is a sphere, and the
 * whole bouncy castle costs less than one properly tessellated one would.
 */
function blob(
  rx: number,
  ry: number,
  rz: number,
  x = 0,
  y = 0,
  z = 0,
  seg = 6,
  rings = 4
): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, seg, rings);
  g.scale(rx, ry, rz);
  g.translate(x, y, z);
  return g;
}

/** A box carrying a pitch about X. Slide beds and scramble ramps are these. */
function slope(
  w: number,
  t: number,
  len: number,
  pitch: number,
  x: number,
  y: number,
  z: number
): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, t, len);
  g.rotateX(pitch);
  g.translate(x, y, z);
  return g;
}

/**
 * A play colour, kept a play colour.
 *
 * `belong` at full strength drags a primary a third of the way into whatever
 * the level's air is, which is right for a cardboard box and wrong for a slide:
 * the equipment in here was specified bright by somebody selling fun, and the
 * point of the level is that it still is. Same reasoning and roughly the same
 * numbers as `PropPalette.carton` and `PropPalette.hazard`.
 */
function party(hex: number, pal: PropPalette): THREE.Color {
  return belong(hex, pal, pal.pull * 0.35, 1.02);
}

/**
 * Wipe-clean moulded plastic. The one thing in this file worth a second shader:
 * soft-play equipment is specified to be hosed down, and a Lambert surface with
 * no highlight anywhere on it reads as felt. Four materials per level, shared by
 * every instance of the kinds that use them.
 */
function gloss(color: THREE.Color): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.07,
    roughness: 0.4,
    metalness: 0,
  });
}

/** The venue's own colours, before anything is built out of them. */
interface FunKit {
  red: THREE.Color;
  yellow: THREE.Color;
  blue: THREE.Color;
  green: THREE.Color;
  orange: THREE.Color;
  violet: THREE.Color;
  /** Paper goods: cloths, plates, the cake. Warm white rather than white. */
  cream: THREE.Color;
}

function funKit(pal: PropPalette): FunKit {
  return {
    red: party(0xd8342c, pal),
    yellow: party(0xf2b91f, pal),
    blue: party(0x2f6fd0, pal),
    green: party(0x3fa34a, pal),
    orange: party(0xe8752a, pal),
    violet: party(0x8f4fbe, pal),
    cream: belong(0xf6efdc, pal, pal.pull * 0.5, 1.0),
  };
}

/* ================================================================== *
 * Soft play
 * ================================================================== */

/**
 * The climbing frame, as a module rather than as an object.
 *
 * This kind used to be a 3 m free-standing frame that stood in the middle of a
 * bay with twenty-five metres of carpet round it, and that was the single thing
 * most wrong with the level: a soft-play centre is not a hall containing a
 * climbing frame, it is a hall that *is* one. So the model is now one bay of a
 * frame, sized to the level's own cell, and `world/gen/funrooms.ts` tiles it.
 * Ten of them in a block are one frame twenty metres across with a continuous
 * first floor over it; one on its own is a padded post.
 *
 * Everything about the geometry follows from one constraint, which is the reason
 * the level can be filled at all: **the module touches the floor only at its
 * column.** The deck, the netting, the tube runs and the canopies all live at
 * 2.11 m and above — the same head height the tunnels on this level already use
 * — so the collision box is the column and nothing else, and the player walks
 * under the whole frame with 2.8 m of clear floor between any two posts. The
 * volume is full; the plan is still passable.
 *
 * Which also means the model is deliberately far wider than its footprint, and
 * that is not the usual bug. Re-read `PROP_DEFS['play-structure']` before
 * changing any height here: anything that drops below about 2.05 m outside the
 * column's own radius becomes an invisible wall.
 */

/** Deck top, its thickness, and the head height under it. */
const PS_DECK = 2.25;
const PS_DECK_T = 0.14;
/** Where the netting above a deck stops, and where an upper deck sits. */
const PS_NET_TOP = 3.24;
const PS_UPPER = 3.4;
/** Padded column: the foam sleeve's radius, its height, and the steel inside. */
const PS_PAD_R = 0.3;
const PS_PAD_H = 1.32;
const PS_CORE_R = 0.13;

/**
 * A climbing net stretched flat: five strands one way, three the other. A grid
 * of thin boxes rather than anything woven, because at four metres a net *is* a
 * grid of thin boxes.
 */
function psNet(
  axis: 'x' | 'z',
  at: number,
  from: number,
  to: number,
  y0: number,
  y1: number
): THREE.BufferGeometry[] {
  const parts: THREE.BufferGeometry[] = [];
  const T = 0.035;
  const mid = (y0 + y1) / 2;
  const span = to - from;
  for (let i = 0; i < 6; i++) {
    const u = from + (span * (i + 0.5)) / 6;
    parts.push(axis === 'z' ? box(T, y1 - y0, T, u, mid, at) : box(T, y1 - y0, T, at, mid, u));
  }
  for (let j = 0; j < 3; j++) {
    const y = y0 + ((y1 - y0) * (j + 1)) / 4;
    parts.push(
      axis === 'z' ? box(span, T, T, from + span / 2, y, at) : box(T, T, span, at, y, from + span / 2)
    );
  }
  return parts;
}

/** A ladder wrapped onto the column, so the way up stays inside the box. */
function psLadder(y0: number, y1: number, rungs: number): THREE.BufferGeometry[] {
  const parts: THREE.BufferGeometry[] = [
    box(0.05, y1 - y0, 0.05, -0.26, (y0 + y1) / 2, -0.24),
    box(0.05, y1 - y0, 0.05, 0.26, (y0 + y1) / 2, -0.24),
  ];
  for (let i = 0; i < rungs; i++) {
    parts.push(box(0.52, 0.045, 0.045, 0, y0 + ((y1 - y0) * (i + 1)) / (rungs + 1), -0.24));
  }
  return parts;
}

export function buildPlayStructure(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);
  // The module is a cell of the building. A 2 cm shadow gap at each edge keeps
  // two abutting decks off exactly coincident faces.
  const H = spec.cellSize * 0.5 - 0.01;

  /** The padded column, from the carpet up to the underside of the deck. */
  const column = (top: number): THREE.BufferGeometry[] => [
    cyl(PS_CORE_R, PS_CORE_R, top, 8, 0, top / 2, 0),
    cyl(PS_PAD_R, PS_PAD_R, PS_PAD_H, 10, 0, PS_PAD_H / 2, 0),
    // A shallow dome on the foot, so the padding reads as fitted rather than as
    // the post simply being fatter down there.
    blob(PS_PAD_R, 0.1, PS_PAD_R, 0, PS_PAD_H, 0, 10, 3),
  ];

  /** A post with nothing on it: a bumper, which is what a frame's edge is. */
  const lonePost = merge([
    ...column(2.34),
    blob(0.24, 0.24, 0.24, 0, 2.42, 0, 8, 4),
    cyl(0.2, 0.2, 0.05, 10, 0, PS_PAD_H + 0.12, 0),
  ]);

  /** The deck slab and the joists carrying it. Spans the whole module. */
  const deck = (): THREE.BufferGeometry[] => [
    box(H * 2, PS_DECK_T, H * 2, 0, PS_DECK - PS_DECK_T / 2, 0),
    box(H * 2, 0.1, 0.1, 0, PS_DECK - PS_DECK_T - 0.05, -H * 0.55),
    box(H * 2, 0.1, 0.1, 0, PS_DECK - PS_DECK_T - 0.05, H * 0.55),
    box(0.1, 0.1, H * 2, 0, PS_DECK - PS_DECK_T - 0.05, 0),
    // Braces from the head of the column out to the four deck corners. A three
    // and a half metre platform standing on one post is a mushroom without
    // them, and they are the only part of the module that could get in the way
    // — so they run from 1.98 m to the deck soffit and never dip below either.
    ...[-1, 1].flatMap((sx) =>
      [-1, 1].map((sz) =>
        strut(0.055, 0, 1.98, 0, sx * H * 0.9, PS_DECK - PS_DECK_T, sz * H * 0.9, 5)
      )
    ),
  ];
  const deckGeo = merge(deck());

  /** The upper mezzanine, over half the module, on four short legs. */
  const upper = merge([
    box(H, 0.1, H * 2, -H * 0.5, PS_UPPER, 0),
    box(0.09, PS_UPPER - PS_DECK, 0.09, -H * 0.94, (PS_UPPER + PS_DECK) / 2, -H * 0.94),
    box(0.09, PS_UPPER - PS_DECK, 0.09, -H * 0.94, (PS_UPPER + PS_DECK) / 2, H * 0.94),
    box(0.09, PS_UPPER - PS_DECK, 0.09, -0.06, (PS_UPPER + PS_DECK) / 2, -H * 0.94),
    box(0.09, PS_UPPER - PS_DECK, 0.09, -0.06, (PS_UPPER + PS_DECK) / 2, H * 0.94),
  ]);

  /** A flat canopy over the deck, on four legs that start above head height. */
  const canopy = merge([
    box(H * 2, 0.07, H * 2, 0, PS_UPPER + 0.18, 0),
    ...[-1, 1].flatMap((sx) =>
      [-1, 1].map((sz) =>
        box(0.08, PS_UPPER + 0.14 - PS_DECK, 0.08, sx * H * 0.9, (PS_UPPER + 0.14 + PS_DECK) / 2, sz * H * 0.9)
      )
    ),
  ]);

  /**
   * A tube run crossing the module along local X at deck-plus-one height. Its
   * ends are at the module edge, so a row of these with the same facing is a
   * continuous tunnel over the frame rather than four loose sections.
   */
  const tubeRun = (): THREE.BufferGeometry => {
    const g = new THREE.CylinderGeometry(0.44, 0.44, H * 2, 12, 1, true);
    g.rotateZ(Math.PI / 2);
    g.translate(0, PS_DECK + 0.62, 0);
    return g;
  };
  const tubeCollars = merge(
    [-1, 1].map((sx) => {
      const g = new THREE.CylinderGeometry(0.48, 0.48, 0.12, 12, 1, true);
      g.rotateZ(Math.PI / 2);
      g.translate(sx * (H - 0.06), PS_DECK + 0.62, 0);
      return g;
    })
  );
  /** The cradle holding the tube up off the deck it crosses. */
  const tubeLegs = merge(
    [-1, 1].map((sx) => box(0.1, 0.18, 0.5, sx * H * 0.6, PS_DECK + 0.09, 0))
  );

  /** Netting along one edge of the deck, in local -Z, and round the corner. */
  const netZ = merge(psNet('z', -H + 0.06, -H, H, PS_DECK + 0.02, PS_NET_TOP));
  const netCorner = merge([
    ...psNet('z', -H + 0.06, -H, H, PS_DECK + 0.02, PS_NET_TOP),
    ...psNet('x', -H + 0.06, -H, H, PS_DECK + 0.02, PS_NET_TOP),
  ]);
  /**
   * The rail the netting is laced to, and the fascia under it.
   *
   * The fascia is the reason a block of these reads as a first floor rather than
   * as a low green ceiling. Standing under a frame you are looking at a slab
   * soffit and nothing else — the netting, the tubes and the mezzanines are all
   * *above* the deck and completely hidden from below — so an edge module hangs
   * a bright band off the deck's outer face. That band is the only part of the
   * whole module a player at eye height ever sees square on, and it is what
   * draws the outline of the frame across the room.
   *
   * Its underside is at 1.98 m — the same hard floor the braces keep, and the
   * only headroom the module has to give. That is 13 cm of band, which is thin,
   * so it is thrown a few centimetres proud of the deck edge as well: a nosing
   * catches the ceiling lights along its whole length and reads from across a
   * hall in a way that a flush strip does not.
   */
  const FASCIA_H = PS_DECK - PS_DECK_T - 1.98;
  const fasciaY = 1.98 + FASCIA_H / 2;
  const fasciaZ = box(H * 2, FASCIA_H, 0.17, 0, fasciaY, -H + 0.04);
  const fasciaX = box(0.17, FASCIA_H, H * 2, -H + 0.04, fasciaY, 0);
  const railZ = merge([
    box(H * 2, 0.08, 0.08, 0, PS_NET_TOP, -H + 0.06),
    box(H * 2, 0.09, 0.09, 0, PS_DECK + 0.05, -H + 0.06),
    fasciaZ,
  ]);
  const railCorner = merge([
    box(H * 2, 0.08, 0.08, 0, PS_NET_TOP, -H + 0.06),
    box(H * 2, 0.09, 0.09, 0, PS_DECK + 0.05, -H + 0.06),
    fasciaZ,
    box(0.08, 0.08, H * 2, -H + 0.06, PS_NET_TOP, 0),
    box(0.09, 0.09, H * 2, -H + 0.06, PS_DECK + 0.05, 0),
    fasciaX,
  ]);

  // Variant order is the module palette `funrooms.ts` addresses by index.
  //  0 post   1 deck   2 deck+net   3 deck+corner net
  //  4 deck+tube   5 deck+upper   6 deck+ladder   7 deck+canopy
  // One buffer for the column, shared by every variant that has a plain one:
  // `makeShape` disposes a set, so a geometry named twice is freed once.
  const post = merge(column(PS_DECK));
  const red = [
    lonePost,
    post,
    post,
    post,
    post,
    merge([...column(PS_DECK), box(0.11, PS_UPPER - PS_DECK, 0.11, 0, (PS_UPPER + PS_DECK) / 2, 0)]),
    post,
    post,
  ];
  const green = [null, deckGeo, deckGeo, deckGeo, deckGeo, merge([...deck(), upper]), deckGeo, merge([...deck(), canopy])];
  const blue = [
    null,
    null,
    netZ,
    netCorner,
    netZ,
    merge([...psNet('z', -H + 0.06, -H, H, PS_DECK + 0.02, PS_NET_TOP), ...psNet('x', H - 0.06, -H, H, PS_UPPER + 0.02, PS_UPPER + 0.7)]),
    netZ,
    netZ,
  ];
  const yellow = [
    null,
    null,
    railZ,
    railCorner,
    merge([railZ, tubeCollars, tubeLegs]),
    railZ,
    merge([railZ, ...psLadder(0.18, PS_DECK, 6)]),
    railZ,
  ];

  return makeShape(
    spec,
    // No jitter of any kind. A module whose deck is a degree off its neighbour's
    // is a first floor with a crack down it.
    { kind: 'floor' },
    [
      layer(gloss(kit.red), red),
      layer(gloss(kit.green), green),
      layer(matte(kit.blue, 0.08), blue),
      layer(matte(kit.yellow), yellow),
      layer(
        new THREE.MeshLambertMaterial({
          color: kit.violet,
          emissive: kit.violet,
          emissiveIntensity: 0.06,
          transparent: true,
          opacity: 0.42,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
        [null, null, null, null, tubeRun(), null, null, null]
      ),
    ]
  );
}

/**
 * A moulded plastic slide, coming off the first floor of the frame.
 *
 * Deliberately not the level-transition `slide` in `propslide.ts`, which is a
 * steel chute cut through the floor with signage bolted to it. This one is
 * equipment: a platform, a bed and a run-out, in one bright colour.
 *
 * The platform is at 2.25 m, which is not a number picked for looks — it is
 * `PS_DECK`, the height of a frame module's deck. `world/gen/funrooms.ts` puts
 * these against the edge of a frame block with the platform facing into it, so
 * the slide is the way *down off the frame* rather than a slide that happens to
 * be standing near one. Four metres of bed at 29° is what getting down from
 * that height actually takes, which is why this is one of the longest props in
 * the game and why it needs eight clear cells around it to be placed at all.
 *
 * The three variants are three colours of one mould, which is how a venue buys
 * them. Each colour is its own layer with the other two nulled out, so an
 * instance draws exactly one bed and the geometry is still shared — the two
 * unused meshes are `visible = false` and cost nothing to render.
 */
/** Platform height. Deliberately equal to `PS_DECK`: this comes off the frame. */
const SL_TOP = 2.25;
/**
 * Bed pitch, and its length down the slope.
 *
 * Nothing here is symmetric about anything, and `PropFootprint` has no offset
 * field: the contract in `world/gen/propTables.ts` is that a kind carrying a box
 * is authored centred on its own origin in XZ. So the coordinates below already
 * carry a 0.37 m shift toward the run-out, which is what puts the middle of the
 * prop's 4.3 m length on the origin rather than under its platform. Nothing
 * reaches past 2.13 m on Z or 0.47 m on X. Re-measure if any of it moves.
 */
const SL_PITCH = -0.6;
const SL_RUN = 3.68;

export function buildPlaySlide(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  const bed = (): THREE.BufferGeometry[] => [
    slope(0.88, 0.08, SL_RUN, SL_PITCH, 0, 1.17, -0.03),
    // Kerbs, which are what make it read as moulded rather than as a plank.
    slope(0.09, 0.26, SL_RUN, SL_PITCH, -0.4, 1.26, -0.03),
    slope(0.09, 0.26, SL_RUN, SL_PITCH, 0.4, 1.26, -0.03),
    // The flat run-out, and the nose that stops it being a cliff.
    box(0.88, 0.08, 0.52, 0, 0.09, -1.81),
    box(0.09, 0.22, 0.52, -0.4, 0.19, -1.81),
    box(0.09, 0.22, 0.52, 0.4, 0.19, -1.81),
    rodX(0.06, 0.88, 6, 0, 0.1, -2.07),
  ];

  const tower = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [
      box(0.86, 0.08, 0.66, 0, SL_TOP, 1.82),
      // Four legs, and a fifth under the middle of the bed: three metres of
      // chute with nothing under it is a diving board.
      ...[-0.42, 0.42].flatMap((x) => [
        box(0.11, SL_TOP, 0.11, x, SL_TOP / 2, 1.57),
        box(0.11, SL_TOP, 0.11, x, SL_TOP / 2, 2.07),
      ]),
      box(0.12, 1.1, 0.12, 0, 0.55, -0.43),
      // Hand rails at the head of the ladder.
      box(0.07, 0.66, 0.07, -0.42, SL_TOP + 0.37, 1.57),
      box(0.07, 0.66, 0.07, 0.42, SL_TOP + 0.37, 1.57),
      box(0.91, 0.07, 0.07, 0, SL_TOP + 0.68, 1.57),
    ];
    // The ladder up the back, which is the half of this prop that says the
    // platform is somewhere a person was expected to get to.
    for (let i = 0; i < 6; i++) parts.push(box(0.72, 0.06, 0.1, 0, 0.3 + i * 0.33, 2.07));
    return parts;
  };

  const plain = merge(tower());
  const hooded = merge([
    ...tower(),
    // An arch over the mouth of the chute. Every third one in a catalogue has
    // one, and it is what breaks up a room with two slides in it.
    box(0.1, 0.72, 0.1, -0.42, SL_TOP + 0.4, 1.42),
    box(0.1, 0.72, 0.1, 0.42, SL_TOP + 0.4, 1.42),
    box(0.94, 0.1, 0.1, 0, SL_TOP + 0.71, 1.42),
  ]);

  return makeShape(
    spec,
    { kind: 'floor' },
    [
      layer(matte(kit.blue), [plain, hooded, plain]),
      layer(gloss(kit.red), [merge(bed()), null, null]),
      layer(gloss(kit.yellow), [null, merge(bed()), null]),
      layer(gloss(kit.green), [null, null, merge(bed())]),
    ]
  );
}

/**
 * Ball radius, and how deep the ball level lies.
 *
 * `BP_DEPTH` is the one number that decides whether this reads as a pool or as
 * a rash of dots on the carpet, and 0.26 m was the latter: at standing eye
 * height a quarter-metre of balls is something you notice you are walking over
 * rather than something you are in. A real pool comes to an adult's knee, so
 * this is set to put the surface of the mass at about 0.6 m — deep enough that
 * the far side of a pool hides the carpet completely and the player's own feet
 * go under. It costs nothing in collision, because the kind has no footprint.
 */
const BP_BALL = 0.115;
const BP_DEPTH = 0.42;

/**
 * How full a tile of the ball field is at a point, given in tile coordinates
 * that run -1..1 on both axes. Everything about the field's shape lives here:
 * the tiles themselves are identical squares, and the density profile is what
 * decides whether one is the middle of a pool, its edge, or a handful of balls
 * that rolled out onto the carpet.
 */
type BallField = (u: number, v: number) => number;

/** 1 in the deep end, falling to almost nothing as `v` runs to +1. */
function fieldEdge(u: number, v: number): number {
  void u;
  const t = (v + 1) / 2;
  return clamp01(1.12 - t * t * 1.5);
}

/**
 * A tile of the ball field.
 *
 * The old model was a 3.5 m walled pit with a half-metre collision box round
 * it, which meant the one object on the level a player would obviously want to
 * get into was the one they had to walk around. The wall is gone, the box is
 * gone (see `PROP_DEFS['ball-pit']`), and what is left is a *field*: four cells
 * of balls per tile, laid edge to edge by `world/gen/funrooms.ts` so a pool is
 * as large as the pass wants it, with the boundary tiles thinning out into
 * loose balls scattered over the carpet. You wade in.
 *
 * Two things carry the volume between them. The **mass** is a lattice of
 * flattened ellipsoids at the ball level — the body of the pool, which is what
 * a hundred and fifty spheres alone stubbornly refuse to look like — and the
 * **crust** is spheres scattered on top of it to break the silhouette. Each
 * ball is ten triangles, because a ball pit is read as a texture of balls and
 * never as any one ball.
 *
 * The balance between the two is the whole trick, and it is easy to get wrong in
 * the direction that turns the pool into a heap of beanbags: at a 7 × 7 lattice
 * the mass lumps were half a metre across, which is four balls wide, and they
 * read as upholstery with a sprinkling of balls on it. The lattice is therefore
 * fine enough that a lump is about two balls across, and the crust is dense
 * enough to hide the ones on the surface. What you should never be able to do is
 * pick out a single ellipsoid.
 */
export function buildBallPit(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);
  // Two cells square: a tile smaller than this is a rug, and the pass that lays
  // them wants a pool measured in tens of metres rather than in objects.
  const H = spec.cellSize;

  /**
   * One fill state, as three colour groups. The mass goes in with the crust so
   * a pool is three buffers rather than six, and the ellipsoids of the mass are
   * spread across all three colours — a single-coloured body under a mixed
   * crust reads as a tarpaulin with balls on it.
   */
  const build = (seed: number, field: BallField, crust: number): THREE.BufferGeometry[][] => {
    const rng = makeRNG(seed);
    const groups: THREE.BufferGeometry[][] = [[], [], []];
    /*
     * The mass: a lattice of flattened lumps whose tops all land on the surface
     * of the pool, `BP_DEPTH × d × 1.42`.
     *
     * Two rules, and getting either wrong is very visible. A lump must be
     * **wider than the lattice spacing**, or the field has holes in it that show
     * blue carpet through the middle of a pool. And a lump must be **wider than
     * it is tall**, or it comes out as a spike: a five-segment sphere stretched
     * upwards is a bipyramid, and a field of those is a bed of nails in primary
     * colours. So the horizontal radius is set from the lattice and the vertical
     * one is capped against it, and the depth of the pool is carried by where
     * the lump sits rather than by how far it is stretched.
     *
     * The lattice is also jittered and the lumps are drawn at varying size,
     * because a pool looked down into from inside it is the one view where a
     * regular grid of identical domes is unmistakable — it comes out as tiled
     * upholstery. Neither is free: the jitter has to stay small enough, against
     * the overlap the radius buys, that it never opens a pinhole through to the
     * carpet in the middle of a pool.
     *
     * Five segments and two rings, because this is under the crust and only ever
     * contributes colour and opacity.
     */
    const N = 20;
    const r0 = (H / N) * 1.85;
    // The mass has to reach the tile's own edge, not stop short of it. At 0.86
    // the outermost lump sat 0.6 m inside the boundary, so two abutting tiles
    // left a metre-wide channel of blue carpet between them and a pool came out
    // ruled into squares. At 1.0 the rim lumps of neighbouring tiles overlap.
    const SPREAD = 1;
    const step = ((2 / N) * H) * SPREAD;
    let k = 0;
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const u = -1 + (2 * (i + 0.5)) / N;
        const v = -1 + (2 * (j + 0.5)) / N;
        const jx = rng.range(-0.18, 0.18) * step;
        const jz = rng.range(-0.18, 0.18) * step;
        const r = r0 * rng.range(0.88, 1.15);
        const d = field(u, v);
        // Under about half full there is no body left, only loose balls — which
        // is exactly what the edge of a pool that nobody has tidied looks like.
        if (d < 0.5) continue;
        const ry = Math.min(BP_DEPTH * d, r * 0.6);
        groups[k++ % 3]!.push(
          blob(r, ry, r, u * H * SPREAD + jx, BP_DEPTH * d * 1.42 - ry, v * H * SPREAD + jz, 5, 2)
        );
      }
    }
    // The crust. Rejection-sampled against the field, so the balls thin out
    // exactly where the body does and the boundary is never a clean rectangle.
    let tries = 0;
    let placed = 0;
    while (placed < crust && tries < crust * 12) {
      tries++;
      const u = rng.range(-1, 1);
      const v = rng.range(-1, 1);
      const d = field(u, v);
      if (rng.next() > d) continue;
      placed++;
      const r = rng.range(BP_BALL - 0.025, BP_BALL);
      // Balls in the body sit *on* it; the strays out on the carpet sit on the
      // floor. The mass reaches 1.42 × depth, so a crust ball has to be centred
      // above 1.3 × depth to break that surface at all — under that it is buried
      // in the very lumps it is there to disguise, which is what it used to be.
      const y = d >= 0.5 ? BP_DEPTH * d * 1.3 : r;
      // Six segments and three rings, not five and two. At five and two a ball
      // is a bipyramid, and the crust is the one part of this the player ever
      // gets within arm's reach of — from inside a pool it reads as a scatter of
      // plastic chips rather than as balls. Twenty-four triangles is the price
      // of the whole effect working at the range it is looked at from.
      groups[placed % 3]!.push(blob(r, r, r, u * H * 0.97, y, v * H * 0.97, 6, 3));
    }
    return groups;
  };

  /*
   * Eight tiles. The first two are the inside of a pool; 2 to 4 are its edges,
   * turned by the placer's yaw to face whichever way the pool stops; 5 and 6
   * are the balls that got out, which go on the carpet *beside* a pool; 7 is a
   * drift piled against one side by whoever last waded through.
   */
  const fields: ReadonlyArray<readonly [BallField, number]> = [
    [() => 1, 300],
    [(u, v) => 0.92 + 0.1 * Math.cos(u * 2.1) * Math.cos(v * 1.7), 280],
    [fieldEdge, 215],
    [(u, v) => Math.min(fieldEdge(u, v), fieldEdge(v, u)), 170],
    [(u, v) => clamp01(1.2 - Math.abs(v) * 1.7) * clamp01(1.15 - Math.abs(u) * 0.3), 160],
    [(u, v) => 0.16 + 0.1 * Math.cos(u * 3.1 + v * 2.2), 40],
    [(u, v) => clamp01(0.34 - 0.22 * (u + v)), 32],
    [(u, v) => clamp01(1.25 - (v + 1) * 1.05) * clamp01(1.3 - Math.abs(u) * 0.6), 185],
  ];
  const fills = fields.map(([field, crust], i) => build(0x4b19 + i * 977, field, crust));

  return makeShape(
    spec,
    // Square to the building, and never turned by anything but the placer: two
    // tiles a few degrees apart leave a wedge of bare carpet between them.
    { kind: 'floor' },
    [
      layer(gloss(kit.red), fills.map((f) => merge(f[0]!))),
      layer(gloss(kit.green), fills.map((f) => merge(f[1]!))),
      layer(gloss(kit.yellow), fills.map((f) => merge(f[2]!))),
    ]
  );
}

/** Tube radius, its length, and how high off the floor its cradles hold it. */
const CT_R = 0.4;
const CT_LEN = 1.84;
const CT_Y = 0.7;

/**
 * A run of translucent tube on short legs.
 *
 * Milky rather than clear on purpose: you can half-see through it and not quite
 * tell whether there is anything inside, and the second variant carries a darker
 * sleeve two thirds of the way along that is either a shadow or is not. It is
 * never resolved, because resolving it would make it a monster.
 *
 * The collar at each end is the building's own detail. A tunnel on this level is
 * a run of `Cell.Doorway`, and the renderer heads one of those and cases it at
 * every 3.4 m seam — so a loose section with a yellow ring at each end is
 * visibly the same system as the walls around it, delivered and never fitted.
 *
 * `PROP_DEFS` gives the kind a run and the placer does extend a free-standing
 * kind along a grid axis, but it steps a whole cell at a time: 3.4 m against a
 * 1.84 m section. A run therefore comes out as sections set down in a line with
 * air between them rather than as a continuous tube, and that is the right thing
 * to author for. The tunnels here are architecture; what is standing in the bays
 * is stock nobody put up.
 */
export function buildCrawlTube(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  const cradle = (z: number, h: number): THREE.BufferGeometry[] => [
    box(0.72, 0.06, 0.14, 0, h, z),
    box(0.09, h, 0.14, -0.31, h / 2, z),
    box(0.09, h, 0.14, 0.31, h / 2, z),
  ];
  const legsLevel = merge([...cradle(-0.62, 0.28), ...cradle(0.62, 0.28)]);
  const legsLame = merge([...cradle(-0.62, 0.28), ...cradle(0.62, 0.17)]);

  /**
   * Put a part built about the origin into place. A sagging tube has to be
   * rotated before it is lifted, or the rotation swings the whole length
   * sideways and the far end walks out of the collision box.
   */
  const pose = (g: THREE.BufferGeometry, tilt: number): THREE.BufferGeometry => {
    if (tilt) g.rotateX(tilt);
    g.translate(0, CT_Y - (tilt ? 0.06 : 0), 0);
    return g;
  };

  const shell = (tilt: number): THREE.BufferGeometry => pose(tubeZ(CT_R, CT_LEN, 10, 0), tilt);
  const collars = (both: boolean, tilt: number): THREE.BufferGeometry => {
    const end = CT_LEN / 2 - 0.06;
    const parts = [tubeZ(CT_R + 0.04, 0.12, 10, 0).translate(0, 0, -end)];
    if (both) parts.push(tubeZ(CT_R + 0.04, 0.12, 10, 0).translate(0, 0, end));
    return pose(merge(parts), tilt);
  };
  const inside = pose(tubeZ(CT_R - 0.05, 0.62, 10, 0).translate(0, -0.02, 0.24), 0);

  const milky = new THREE.MeshLambertMaterial({
    color: kit.green,
    emissive: kit.green,
    emissiveIntensity: 0.06,
    transparent: true,
    opacity: 0.46,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  return makeShape(spec, { kind: 'floor' }, [
    layer(matte(pal.metal), [legsLevel, legsLevel, legsLame]),
    layer(matte(kit.yellow), [collars(true, 0), collars(true, 0), collars(false, 0.05)]),
    layer(matte(pal.plasticDark, 0.02), [null, inside, null]),
    layer(milky, [shell(0), shell(0), shell(0.05)]),
  ]);
}

/**
 * Foam play shapes, stacked or knocked over.
 *
 * Nothing reaches past 0.31 m from the origin on either axis, which leaves the
 * 0.42 m box enough slack for the render-only yaw jitter: the box does not turn
 * with the model, and a 0.3 rad swing puts the worst corner at 0.39.
 */
export function buildSoftBlocks(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  // Stacked, by somebody tidying up.
  const stackA = box(0.44, 0.3, 0.44, 0, 0.15, 0);
  const stackB = cyl(0.19, 0.19, 0.15, 8, 0.02, 0.375, -0.01);
  const stackC = box(0.3, 0.09, 0.3, -0.01, 0.495, 0.02);

  // Knocked over, by somebody who was not.
  const downA = box(0.44, 0.3, 0.44, -0.08, 0.15, 0.06);
  const downB = rodX(0.16, 0.34, 8, 0.14, 0.16, -0.14);
  const downC = box(0.34, 0.14, 0.34, 0.14, 0.07, 0.14);

  // An arch, which every foam set has and nobody ever stacks back up.
  const archA = merge([box(0.14, 0.4, 0.3, -0.24, 0.2, 0), box(0.14, 0.4, 0.3, 0.24, 0.2, 0)]);
  const archB = box(0.62, 0.12, 0.3, 0, 0.46, 0);
  const archC = box(0.24, 0.1, 0.24, 0, 0.05, 0);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.3 }, [
    layer(matte(kit.red), [stackA, downA, archA]),
    layer(matte(kit.yellow), [stackB, downB, archB]),
    layer(matte(kit.blue), [stackC, downC, archC]),
  ]);
}

/* ================================================================== *
 * Party dressing
 * ================================================================== */

/** One balloon: a slightly pear-shaped ellipsoid with a neck under it. */
function balloonAt(r: number, x: number, y: number, z: number): THREE.BufferGeometry[] {
  return [blob(r, r * 1.14, r, x, y, z, 6, 3), cyl(0.012, 0.03, 0.05, 4, x, y - r * 1.14 - 0.02, z)];
}

/** A string, bent through one intermediate point so it is not a wire. */
function stringTo(
  x: number,
  y: number,
  z: number,
  mx: number,
  my: number,
  mz: number
): THREE.BufferGeometry[] {
  return [strut(0.005, 0, 0.09, 0, mx, my, mz, 4), strut(0.005, mx, my, mz, x, y, z, 4)];
}

/** Where one balloon in a bunch sits, and where its string bends on the way. */
interface Balloon {
  r: number;
  p: readonly [number, number, number];
  m: readonly [number, number, number];
}

/**
 * A bunch on a weight, with the helium long gone.
 *
 * This is the single most effective detail on the level, and it is entirely in
 * the heights: one or two still nudging the ends of their strings, the rest
 * sagged to head height or lying against the weight. Drawn all-up it is
 * decoration; drawn all-down it is rubbish. The mix is a party that finished at
 * some point nobody was there for.
 *
 * Every variant keeps one balloon near the top of the 1.5 m box, because one box
 * per kind has to cover the tallest thing the kind can be.
 */
export function buildBalloons(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  const weight = (): THREE.BufferGeometry[] => [
    cyl(0.1, 0.14, 0.09, 8, 0, 0.045, 0),
    cyl(0.03, 0.03, 0.06, 6, 0, 0.11, 0),
  ];
  const tipped = (): THREE.BufferGeometry[] => {
    const parts = [cyl(0.1, 0.14, 0.09, 8, 0, 0, 0), cyl(0.03, 0.03, 0.06, 6, 0, 0.07, 0)];
    tumble(parts, 0.1, 1.44, 0.3);
    settle(parts);
    return parts;
  };

  const mostlyUp: readonly Balloon[] = [
    { r: 0.115, p: [0.05, 1.33, 0.03], m: [0.02, 0.72, 0.02] },
    { r: 0.105, p: [-0.08, 1.14, 0.06], m: [-0.03, 0.6, 0.03] },
    { r: 0.11, p: [0.03, 0.96, -0.09], m: [0.01, 0.52, -0.04] },
    { r: 0.1, p: [-0.06, 0.24, -0.08], m: [-0.04, 0.4, -0.05] },
    { r: 0.1, p: [0.08, 0.12, 0.07], m: [0.05, 0.35, 0.04] },
  ];
  const mostlyDown: readonly Balloon[] = [
    { r: 0.115, p: [-0.03, 1.36, 0.02], m: [-0.01, 0.74, 0.01] },
    { r: 0.105, p: [0.09, 0.13, 0.04], m: [0.06, 0.42, 0.03] },
    { r: 0.1, p: [-0.09, 0.11, -0.05], m: [-0.05, 0.36, -0.03] },
    { r: 0.1, p: [0.02, 0.12, 0.11], m: [0.02, 0.3, 0.06] },
    { r: 0.095, p: [-0.04, 0.3, 0.09], m: [-0.02, 0.46, 0.05] },
  ];
  const half: readonly Balloon[] = [
    { r: 0.115, p: [0.04, 1.29, -0.04], m: [0.02, 0.7, -0.02] },
    { r: 0.11, p: [-0.07, 0.86, 0.05], m: [-0.03, 0.5, 0.03] },
    { r: 0.1, p: [0.07, 0.14, -0.07], m: [0.05, 0.38, -0.04] },
    { r: 0.1, p: [-0.05, 0.12, 0.08], m: [-0.03, 0.32, 0.05] },
    { r: 0.095, p: [0.1, 0.11, 0.02], m: [0.06, 0.28, 0.01] },
  ];

  /** The balloons of a bunch that this colour layer draws. */
  const group = (set: readonly Balloon[], which: readonly number[]): THREE.BufferGeometry => {
    const parts: THREE.BufferGeometry[] = [];
    for (const i of which) {
      const b = set[i]!;
      parts.push(...balloonAt(b.r, b.p[0], b.p[1], b.p[2]));
    }
    return merge(parts);
  };
  const strings = (set: readonly Balloon[], base: () => THREE.BufferGeometry[]): THREE.BufferGeometry => {
    const parts = base();
    for (const b of set) parts.push(...stringTo(b.p[0], b.p[1] - b.r * 1.2, b.p[2], b.m[0], b.m[1], b.m[2]));
    return merge(parts);
  };

  return makeShape(spec, { kind: 'floor', yawJitter: 0.9 }, [
    layer(matte(kit.red), [group(mostlyUp, [0, 3]), group(mostlyDown, [0, 2]), group(half, [0, 4])]),
    layer(matte(kit.yellow), [group(mostlyUp, [1, 4]), group(mostlyDown, [1, 4]), group(half, [1, 2])]),
    layer(matte(kit.violet), [group(mostlyUp, [2]), group(mostlyDown, [3]), group(half, [3])]),
    layer(matte(pal.dark), [strings(mostlyUp, weight), strings(mostlyDown, weight), strings(half, tipped)]),
  ]);
}

/**
 * Paper chains and crepe from the ceiling.
 *
 * A chain is a row of small boxes turned alternately a quarter turn, which is
 * exactly what a paper chain looks like from four metres below one and costs a
 * twentieth of what modelled links would. Nothing here hangs below about 2.7 m,
 * which matters: these carry no footprint, so anything the player could reach
 * would be something they walk straight through.
 */
export function buildStreamers(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  /**
   * A chain sagging between two ceiling points, returned as its even links and
   * its odd ones — a paper chain is always made in two colours.
   */
  /** One link, and the gap between two of them. A chain is links, not beads. */
  const LINK = 0.115;

  const chain = (
    ax: number,
    az: number,
    bx: number,
    bz: number,
    sag: number
  ): [THREE.BufferGeometry[], THREE.BufferGeometry[]] => {
    // Link count follows the span rather than being fixed, so a long chain is a
    // longer chain and not thirteen paper beads on a wire. Contiguous by
    // construction: the step is one link, and a chain with daylight between its
    // links is a string of bunting.
    const run = Math.hypot(bx - ax, bz - az);
    const links = Math.max(8, Math.round(run / LINK));
    const even: THREE.BufferGeometry[] = [];
    const odd: THREE.BufferGeometry[] = [];
    for (let i = 0; i < links; i++) {
      const t = (i + 0.5) / links;
      const x = ax + (bx - ax) * t;
      const z = az + (bz - az) * t;
      const y = -sag * 4 * t * (1 - t) - 0.05;
      const flat = i % 2 === 0;
      (flat ? even : odd).push(
        flat ? box(0.135, 0.1, 0.02, x, y, z) : box(0.02, 0.1, 0.135, x, y, z)
      );
    }
    return [even, odd];
  };

  /** A length of crepe hanging straight down, with the turn it always has. */
  const crepe = (x: number, z: number, drop: number, turn: number): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) {
      const g = box(0.15, drop / 4, 0.01);
      // Each section further round than the last. Without it a streamer is a
      // strip of card, and the turn is the only thing that catches the light.
      g.rotateY(turn * i);
      g.translate(x, -0.05 - (drop / 4) * (i + 0.5), z);
      parts.push(g);
    }
    return parts;
  };

  // Spans of a couple of metres read as a handful of confetti stuck to the
  // ceiling of a hall twenty metres across. These reach most of the way over a
  // party's worth of floor, which is what a room somebody decorated looks like.
  const R = 1.75;
  const [a0, a1] = chain(-R, -R, R, R, 0.62);
  const [b0, b1] = chain(-R, R, R, -R, 0.54);
  const [c0, c1] = chain(-R * 1.05, 0.2, R * 1.05, -0.2, 1.05);
  const [d0, d1] = chain(-R * 0.9, -R * 0.75, R * 0.7, R * 0.95, 0.82);

  const crossedRed = merge([...a0, ...b0]);
  const crossedYellow = merge([...a1, ...b1, ...crepe(0, 0, 0.95, 0.8)]);
  const fallenRed = merge(c0);
  const fallenYellow = merge([...c1, ...crepe(-0.5, 0.35, 1.02, 0.7), ...crepe(0.42, -0.3, 0.78, -0.9)]);
  // The third state is a chain that has come down at one end and taken most of
  // the crepe with it — still a decoration, which is what the sparse version of
  // this was not: three strips of paper on a bare ceiling read as nothing.
  const sparseRed = merge([...d0, ...crepe(-0.35, -0.35, 1.0, 0.6)]);
  const sparseYellow = merge([
    ...d1,
    ...crepe(0.4, 0.28, 0.72, -0.8),
    ...crepe(-0.44, 0.3, 1.06, -0.6),
  ]);

  return makeShape(spec, { kind: 'ceiling', out: 0.02, yawJitter: Math.PI }, [
    layer(matte(kit.red, 0.09), [crossedRed, fallenRed, sparseRed]),
    layer(matte(kit.yellow, 0.09), [crossedYellow, fallenYellow, sparseYellow]),
  ]);
}

/**
 * A banner across the wall.
 *
 * Drawn rather than modelled for the reason the graffiti is: a banner sags, and
 * its lettering sags with it, and neither survives being built out of boxes. See
 * `propfundecals.ts` for the atlas. Two of the six rows are hanging wrong — one
 * end down, and one with a bite out of the middle — so the same wall does not
 * read the same in every room.
 *
 * A whisper of self-illumination, because printed party card is the only surface
 * in this building that was made to be looked at.
 */
export function buildBanner(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const atlas = makeBannerAtlas((hex) => css(belong(hex, pal, pal.pull * 0.35, 1.02)));
  const rng = makeRNG(0x3c81);

  // The last rows of the atlas are the venue's wall lining rather than banners,
  // and a padded panel is four times the height of a ribbon — so the quad size
  // comes per row. The lining is also hung dead square: a banner that is a
  // degree off reads as hung by hand, and a wall panel that is reads as falling
  // off, which is the one thing nothing on this level is allowed to be doing.
  const variants = atlas.entries.map((entry, i) => {
    const metres = BANNER_METRES[i] ?? 0.62;
    const swag = i < BANNER_LINING_FROM;
    return wallDecal(entry, metres, -0.03, swag ? rng.range(-0.03, 0.03) : 0);
  });
  const material = new THREE.MeshLambertMaterial({
    map: atlas.texture,
    // Alpha-tested rather than blended: the swag is a hard-edged shape, and it
    // keeps a dozen banners out of the transparency sort.
    alphaTest: 0.35,
    emissive: 0xffffff,
    emissiveMap: atlas.texture,
    emissiveIntensity: 0.12,
  });

  return makeShape(spec, { kind: 'wall', height: 2.3, yawJitter: 0.02 }, [layer(material, variants)], [atlas]);
}

/**
 * A trestle table with the party still on it.
 *
 * Paper cloth, cups set out, plates, and a cake nobody cut. The cake is whole in
 * all three variants, and that is the point of the prop: everything here was
 * *prepared*, and the preparation is the last thing that happened.
 *
 * The tableware stands above the 0.78 m collision box, which is deliberate —
 * 0.78 is the height of the table, the box already stops the player at nearly
 * twice the step-over height, and nothing on the top overhangs the box in plan.
 */
export function buildPartyTable(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);
  const TOP = 0.735;

  const trestle = (): THREE.BufferGeometry[] => [
    box(1.62, 0.035, 0.72, 0, TOP, 0),
    strut(0.024, -0.62, TOP - 0.02, 0, -0.78, 0, -0.26, 5),
    strut(0.024, -0.62, TOP - 0.02, 0, -0.78, 0, 0.26, 5),
    strut(0.024, 0.62, TOP - 0.02, 0, 0.78, 0, -0.26, 5),
    strut(0.024, 0.62, TOP - 0.02, 0, 0.78, 0, 0.26, 5),
    box(1.18, 0.03, 0.03, 0, 0.3, 0),
  ];

  /** The paper cloth, and the skirt over the sides it was pulled down. */
  const cloth = (rucked: boolean, torn: boolean): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [
      rucked ? box(1.56, 0.02, 0.78, 0.04, TOP + 0.027, 0) : box(1.68, 0.02, 0.78, 0, TOP + 0.027, 0),
      box(1.68, 0.19, 0.02, 0, TOP - 0.075, -0.385),
    ];
    if (torn) {
      // Half the near skirt gone, and what is left of it hanging loose.
      parts.push(box(0.8, 0.19, 0.02, -0.42, TOP - 0.075, 0.385));
      const flap = box(0.5, 0.3, 0.02);
      flap.rotateZ(0.4);
      flap.translate(0.5, TOP - 0.16, 0.36);
      parts.push(flap);
    } else {
      parts.push(box(1.68, 0.19, 0.02, 0, TOP - 0.075, 0.385));
    }
    if (rucked) parts.push(box(0.34, 0.06, 0.72, 0.62, TOP + 0.06, 0));
    return parts;
  };

  const PLATE_AT: ReadonlyArray<readonly [number, number]> = [
    [-0.58, -0.18],
    [-0.2, 0.2],
    [0.5, -0.2],
  ];
  const plates = (n: number): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < n; i++) parts.push(cyl(0.09, 0.085, 0.01, 6, PLATE_AT[i]![0], TOP + 0.043, PLATE_AT[i]![1]));
    return parts;
  };

  const CUP_AT: ReadonlyArray<readonly [number, number]> = [
    [-0.72, 0.14],
    [-0.4, -0.22],
    [-0.06, -0.06],
    [0.24, 0.2],
    [0.66, 0.16],
    [0.36, -0.24],
  ];
  const cups = (upright: number, over: number): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < upright; i++) {
      parts.push(cyl(0.038, 0.028, 0.085, 6, CUP_AT[i]![0], TOP + 0.08, CUP_AT[i]![1]));
    }
    for (let i = 0; i < over; i++) {
      const at = CUP_AT[upright + i]!;
      const g = cyl(0.038, 0.028, 0.085, 6, 0, 0, 0);
      g.rotateZ(Math.PI / 2 + i * 0.7);
      g.translate(at[0], TOP + 0.075, at[1]);
      parts.push(g);
    }
    return parts;
  };

  /** Candles standing in the icing. `down` is the one that fell over. */
  const candles = (down: boolean): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const x = 0.1 + Math.cos(a) * 0.085;
      const z = Math.sin(a) * 0.085;
      if (down && i === 2) {
        const g = box(0.014, 0.09, 0.014);
        g.rotateZ(1.4);
        g.translate(x + 0.05, TOP + 0.16, z);
        parts.push(g);
      } else {
        parts.push(box(0.014, 0.09, 0.014, x, TOP + 0.2, z));
      }
    }
    return parts;
  };

  const cake = (): THREE.BufferGeometry[] => [
    cyl(0.19, 0.19, 0.014, 10, 0.1, TOP + 0.045, 0),
    cyl(0.15, 0.15, 0.11, 10, 0.1, TOP + 0.105, 0),
  ];

  const legs = merge(trestle());
  const cakeGeo = merge(cake());
  return makeShape(spec, { kind: 'floor' }, [
    layer(matte(pal.metal), [legs, legs, legs]),
    layer(matte(kit.cream, 0.08), [
      merge([...cloth(false, false), ...plates(3)]),
      merge([...cloth(true, false), ...plates(2)]),
      merge([...cloth(false, true), ...plates(1)]),
    ]),
    layer(matte(kit.red), [
      merge([...cups(6, 0), ...candles(false)]),
      merge([...cups(3, 2), ...candles(false)]),
      merge([...cups(2, 1), ...candles(true)]),
    ]),
    layer(matte(kit.cream, 0.1), [cakeGeo, cakeGeo, cakeGeo]),
  ]);
}

/**
 * An arcade cabinet, dead.
 *
 * The screen is off and the marquee is unlit, which is the whole prop: an arcade
 * machine making no noise is one of the few objects that says "closed" without
 * saying anything. It is a wall mount with a run, so a line of three along a
 * wall reads as an arcade — the one place on this level where the props do the
 * room's job for it.
 */
export function buildArcadeCabinet(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  const shell = (): THREE.BufferGeometry[] => [
    box(0.64, 0.08, 0.58, 0, 0.04, 0),
    box(0.6, 0.92, 0.56, 0, 0.53, 0),
    box(0.6, 0.66, 0.5, 0, 1.31, 0.02),
    box(0.04, 1.66, 0.5, -0.29, 0.85, 0.02),
    box(0.04, 1.66, 0.5, 0.29, 0.85, 0.02),
    // The hood over the marquee, which is the part that reads at ten metres.
    box(0.64, 0.05, 0.22, 0, 1.715, -0.16),
  ];

  const panel = (): THREE.BufferGeometry => {
    const g = box(0.58, 0.09, 0.3);
    g.rotateX(-0.35);
    g.translate(0, 0.99, -0.19);
    return g;
  };

  const marquee = (): THREE.BufferGeometry[] => [box(0.6, 0.2, 0.12, 0, 1.6, -0.15)];
  const marqueeGone = (): THREE.BufferGeometry[] => [
    // The panel is out; what is left is the empty channel it slid into.
    box(0.6, 0.2, 0.04, 0, 1.6, -0.09),
    box(0.03, 0.2, 0.12, -0.29, 1.6, -0.15),
    box(0.03, 0.2, 0.12, 0.29, 1.6, -0.15),
  ];

  const dark = (sticks: boolean, coinOpen: boolean): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [
      // Bezel, the dead screen inside it, and the speaker slot above.
      box(0.56, 0.46, 0.05, 0, 1.34, -0.21),
      box(0.48, 0.38, 0.02, 0, 1.34, -0.238),
      box(0.4, 0.03, 0.02, 0, 1.62, -0.222),
    ];
    if (sticks) {
      parts.push(cyl(0.026, 0.026, 0.11, 5, -0.14, 1.05, -0.27));
      parts.push(blob(0.035, 0.035, 0.035, -0.14, 1.11, -0.27, 5, 3));
      for (let i = 0; i < 3; i++) parts.push(cyl(0.024, 0.024, 0.02, 6, 0.04 + i * 0.09, 1.03, -0.26));
    } else {
      // Stick and buttons out. The holes are still there.
      for (let i = 0; i < 4; i++) parts.push(cyl(0.026, 0.026, 0.012, 6, -0.14 + i * 0.09, 1.0, -0.265));
    }
    if (coinOpen) {
      const door = box(0.26, 0.16, 0.02);
      door.rotateX(-1.1);
      door.translate(0, 0.38, -0.27);
      parts.push(door, box(0.24, 0.14, 0.02, 0, 0.4, -0.26));
    } else {
      parts.push(box(0.26, 0.16, 0.03, 0, 0.4, -0.295));
    }
    return parts;
  };

  /** The edge strip down the front corner of each side panel. */
  const stripe = (): THREE.BufferGeometry[] => [
    box(0.02, 1.5, 0.02, -0.312, 0.85, -0.22),
    box(0.02, 1.5, 0.02, 0.312, 0.85, -0.22),
  ];

  // Barely any wobble. This is a wall-backed box 0.66 m wide inside a 0.68 m
  // collision box, and the box does not turn with it.
  const shellGeo = merge(shell());
  const live = merge(dark(true, false));
  return makeShape(spec, { kind: 'floor', yawJitter: 0.02 }, [
    layer(matte(kit.blue), [shellGeo, shellGeo, shellGeo]),
    layer(matte(pal.dark, 0.02), [live, live, merge(dark(false, true))]),
    layer(matte(kit.yellow), [
      merge([panel(), ...marquee(), ...stripe()]),
      merge([panel(), ...marqueeGone(), ...stripe()]),
      merge([panel(), ...marquee(), ...stripe()]),
    ]),
  ]);
}

/**
 * A bouncy castle, collapsed over its own blower.
 *
 * The unsettling version rather than the fun one, and it is unsettling for a
 * mechanical reason: the blower is still connected. This did not get packed
 * away — it went down with its motor under it, and nobody came back for it. One
 * arch is drawn half standing in the second variant, because something entirely
 * flat reads as a rug rather than as a thing that fell.
 *
 * Every part sits inside a 1.78 m circle, so the square box contains the model
 * at any yaw the render jitter can produce.
 */
export function buildBouncyCastle(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);

  /** The ground sheet: overlapping flattened ellipsoids, never a rectangle. */
  const sheet = (): THREE.BufferGeometry[] => [
    blob(1.5, 0.1, 1.42, 0, 0.06, 0, 7, 4),
    blob(1.12, 0.14, 0.86, -0.3, 0.09, 0.4, 6, 4),
    blob(0.9, 0.13, 1.1, 0.42, 0.08, -0.3, 6, 4),
  ];

  /** The walls, gone down where they stood, or dragged into a ridge. */
  const walls = (folded: boolean): THREE.BufferGeometry[] => {
    if (folded) {
      return [
        blob(1.34, 0.26, 0.38, 0.04, 0.24, -1.0, 6, 4),
        blob(0.34, 0.3, 1.14, -1.1, 0.26, 0.12, 6, 4),
        blob(1.24, 0.34, 0.32, 0.1, 0.3, 0.94, 6, 4),
      ];
    }
    return [
      blob(1.38, 0.22, 0.34, 0, 0.2, -1.08, 6, 4),
      blob(1.38, 0.2, 0.34, 0, 0.18, 1.08, 6, 4),
      blob(0.34, 0.22, 1.16, -1.1, 0.2, 0, 6, 4),
      blob(0.34, 0.21, 1.16, 1.1, 0.19, 0, 6, 4),
    ];
  };

  /**
   * A corner turret, most of the way down.
   *
   * Without these the thing is a red mass on a blue floor, and what it reads as
   * is a paddling pool. The turrets and the blue walls are the whole vocabulary
   * of a bouncy castle: four towers and a rectangle. They are drawn slumped —
   * nothing here is inflated — but the outline survives being flat, which is the
   * only thing a shape lying on the ground has left to say what it was.
   */
  const turret = (x: number, z: number, h: number): THREE.BufferGeometry[] => [
    blob(0.3, h * 0.55, 0.3, x, h * 0.5, z, 6, 4),
    cyl(0.02, 0.24, h * 0.5, 6, x, h * 1.05, z),
  ];

  const turrets = (folded: boolean): THREE.BufferGeometry[] =>
    folded
      ? [...turret(-1.06, -0.98, 0.36), ...turret(1.02, -1.0, 0.3)]
      : [
          ...turret(-1.06, -1.02, 0.46),
          ...turret(1.06, -1.02, 0.4),
          ...turret(-1.06, 1.02, 0.34),
          ...turret(1.06, 1.02, 0.44),
        ];

  /** One arch tube that has not let go yet. */
  const arch = (): THREE.BufferGeometry[] => {
    const pts: ReadonlyArray<readonly [number, number, number]> = [
      [-1.06, 0.16, -0.5],
      [-0.84, 0.5, -0.56],
      [-0.3, 0.66, -0.62],
      [0.34, 0.62, -0.6],
      [0.84, 0.4, -0.54],
      [1.12, 0.14, -0.48],
    ];
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      parts.push(strut(0.15, a[0], a[1], a[2], b[0], b[1], b[2], 6));
    }
    return parts;
  };

  const blower = (exposed: boolean): THREE.BufferGeometry[] => {
    const x = exposed ? 1.16 : 1.06;
    const z = exposed ? 0.88 : 0.96;
    return [
      box(0.36, 0.3, 0.44, x, 0.15, z),
      cyl(0.13, 0.13, 0.1, 8, x - 0.24, 0.17, z, true),
      // The duct, still on the spigot, running back under the sheet.
      strut(0.11, x - 0.26, 0.16, z, x - 0.86, 0.11, z - 0.5, 6),
      strut(0.11, x - 0.86, 0.11, z - 0.5, x - 1.3, 0.09, z - 0.84, 6),
      // The lead, which goes to a socket that is not in this room.
      strut(0.014, x + 0.18, 0.06, z, x + 0.24, 0.02, z - 0.7, 4),
    ];
  };

  return makeShape(spec, { kind: 'floor', yawJitter: 0.05 }, [
    layer(gloss(kit.red), [merge(sheet()), merge(sheet()), merge(sheet())]),
    // The walls are their own primary, so the rectangle they make is still a
    // rectangle when everything in it has gone flat. Green rather than blue:
    // the Funrooms' carpet is blue, and a blue wall on it is no wall at all.
    layer(gloss(kit.green), [merge(walls(false)), merge(walls(false)), merge(walls(true))]),
    layer(gloss(kit.yellow), [
      merge(turrets(false)),
      merge([...turrets(false), ...arch()]),
      merge(turrets(true)),
    ]),
    layer(matte(pal.metalDark), [merge(blower(false)), merge(blower(false)), merge(blower(true))]),
  ]);
}

/**
 * Confetti trodden into the floor.
 *
 * Flat, walked through, and the cheapest prop on the level: one quad and one
 * shared canvas. It is here to say that the room was *used*, which is a
 * different statement from every other piece of dressing in the building —
 * everything else here says the room was prepared.
 */
export function buildConfetti(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const kit = funKit(pal);
  const texture = makeConfettiTexture(
    [css(kit.red), css(kit.yellow), css(kit.blue), css(kit.green), css(kit.orange), css(kit.cream)],
    css(pal.grime),
    0x21ba
  );

  const sizes: ReadonlyArray<readonly [number, number]> = [
    [1.6, 1.3],
    [2.2, 1.7],
    [1.1, 1.0],
  ];
  const variants = sizes.map(([w, d]) => slab(w, d));

  const material = new THREE.MeshLambertMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });

  return makeShape(spec, { kind: 'floor', out: 0.008, yawJitter: Math.PI }, [layer(material, variants)], [texture]);
}
